import { useCubeQuery } from "@cubejs-client/react";

import type { Range } from "types/cube";

export function useResourceCounts(dashboardKey: string, dateRange: Range) {
  const compliantQuery = useCubeQuery({
    measures: ["ResourceCount.resourceCount"],
    timeDimensions: [
      {
        dimension: "ResourceCount.date",
        granularity: "day",
        dateRange,
      },
    ],
    dimensions: ["Dashboard.key", "DashboardSection.key"],
    filters: [
      {
        member: "Dashboard.key",
        operator: "equals",
        values: [dashboardKey],
      },
      {
        member: "DashboardSection.key",
        operator: "set",
      },
    ],
  });

  const nonCompliantQuery = useCubeQuery({
    measures: ["CSO.nonCompliantResourceCount"],
    timeDimensions: [
      {
        dimension: "CSO.date",
        granularity: "day",
        dateRange,
      },
    ],
    filters: [
      {
        member: "CSO.dashboard_key",
        operator: "equals",
        values: [dashboardKey],
      },
    ],
    dimensions: ["CSO.dashboard_key", "CSO.section_key"],
  });

  interface DataPoint {
    y: number;
    x: string;
  }

  interface SeriesPoint {
    value: number;
    x: string;
  }

  interface SeriesItem {
    key: string;
    series: SeriesPoint[];
  }

  type SeriesData = {
    id: string;
    data: DataPoint[];
  }[];

  interface ResultItem {
    compliant: SeriesData;
    nonCompliant: SeriesData;
    efficiency: SeriesData;
  }

  type ResultMap = Map<string, ResultItem>;

  function createEmptyResultItem(): ResultItem {
    return {
      compliant: [],
      nonCompliant: [],
      efficiency: [{ id: "efficiency", data: [] }],
    };
  }

  function addToResultMap(
    resultMap: ResultMap,
    mapKey: string,
    seriesName: "compliant" | "nonCompliant",
    series: SeriesPoint[],
  ) {
    let item = resultMap.get(mapKey);
    if (!item) {
      item = createEmptyResultItem();
      resultMap.set(mapKey, item);
    }
    item[seriesName] = [
      {
        id: seriesName,
        data: series.map((s) => ({ y: s.value, x: s.x })),
      },
    ];
  }

  function processSeries(
    resultMap: ResultMap,
    seriesName: "compliant" | "nonCompliant",
    series?: SeriesItem[] | null,
  ) {
    (series ?? []).forEach((obj) => {
      const [, mapKey] = obj.key.split(","); // section key from key e.g. cost-aws:cost-aws-100,e1-3-4,CostOpportunity.nonCompliantResourceCount
      if (mapKey) {
        addToResultMap(resultMap, mapKey.trim(), seriesName, obj.series);
      }
    });
  }

  function mergeSeriesToMap(
    compliantSeries?: SeriesItem[] | null,
    nonCompliantSeries?: SeriesItem[] | null,
  ): ResultMap {
    const resultMap = new Map<string, ResultItem>();

    processSeries(resultMap, "compliant", compliantSeries);
    processSeries(resultMap, "nonCompliant", nonCompliantSeries);

    calculateEfficiency(resultMap);

    return resultMap;
  }

  function calculateEfficiency(resultMap: Map<string, ResultItem>) {
    resultMap.forEach((item) => {
      const compliantData = item.compliant[0]?.data ?? [];
      const nonCompliantData = item.nonCompliant[0]?.data ?? [];

      // Create maps for efficient lookup by date
      const compliantMap = new Map(compliantData.map((p) => [p.x, p.y]));
      const nonCompliantMap = new Map(nonCompliantData.map((p) => [p.x, p.y]));

      // Get all unique dates, preserving order from compliantData first, then adding any additional from nonCompliantData
      const allDates = [
        ...compliantData.map((p) => p.x),
        ...nonCompliantData
          .map((p) => p.x)
          .filter((date) => !compliantMap.has(date)),
      ];

      const efficiencyData = allDates.map((date) => {
        const compliantCount = compliantMap.get(date) ?? 0;
        const nonCompliantCount = nonCompliantMap.get(date) ?? 0;
        const efficiency = calculateEfficiencyValue(
          compliantCount,
          nonCompliantCount,
          nonCompliantData.length === 0,
        );
        return { x: date, y: efficiency };
      });

      item.efficiency = [{ id: "efficiency", data: efficiencyData }];
    });
  }

  function calculateEfficiencyValue(
    compliantCount: number,
    nonCompliantCount: number,
    isAllCompliant: boolean,
  ): number {
    if (isAllCompliant) return 100;
    if (compliantCount === 0) return 0;
    return Math.round(
      (100 * (compliantCount - nonCompliantCount)) / compliantCount,
    );
  }

  function aggregateResults(resultMap: ResultMap) {
    const aggregatedData: ResultItem = {
      compliant: [{ id: "compliant", data: [] }],
      nonCompliant: [{ id: "nonCompliant", data: [] }],
      efficiency: [{ id: "efficiency", data: [] }],
    };

    function aggregateDataPoints(
      existingData: DataPoint[],
      newData: DataPoint[],
    ): DataPoint[] {
      const aggregated: Record<string, DataPoint> = {};

      [...existingData, ...newData].forEach((point) => {
        if (aggregated[point.x]) {
          aggregated[point.x].y += point.y;
        } else {
          aggregated[point.x] = { ...point };
        }
      });

      return Object.values(aggregated).sort((a, b) => a.x.localeCompare(b.x));
    }

    // Aggregate compliant and nonCompliant data from all keys
    for (const [, value] of resultMap) {
      aggregatedData.compliant[0].data = aggregateDataPoints(
        aggregatedData.compliant[0].data,
        value.compliant[0]?.data ?? [],
      );
      aggregatedData.nonCompliant[0].data = aggregateDataPoints(
        aggregatedData.nonCompliant[0].data,
        value.nonCompliant[0]?.data ?? [],
      );
    }

    // Recalculate efficiency from aggregated counts
    const compliantMap = new Map(
      aggregatedData.compliant[0].data.map((point) => [point.x, point.y]),
    );
    const nonCompliantMap = new Map(
      aggregatedData.nonCompliant[0].data.map((point) => [point.x, point.y]),
    );

    const allDates = new Set([
      ...compliantMap.keys(),
      ...nonCompliantMap.keys(),
    ]);

    aggregatedData.efficiency[0].data = Array.from(allDates)
      .sort((a, b) => a.localeCompare(b))
      .map((date) => {
        const compliantCount = compliantMap.get(date) ?? 0;
        const nonCompliantCount = nonCompliantMap.get(date) ?? 0;
        const efficiency = calculateEfficiencyValue(
          compliantCount,
          nonCompliantCount,
          aggregatedData.nonCompliant[0].data.length === 0,
        );
        return { x: date, y: efficiency };
      });

    if (resultMap.size > 0) {
      resultMap.set("all", aggregatedData);
    }

    return resultMap;
  }

  const compliantSeries = compliantQuery.resultSet?.series() ?? [];
  const nonCompliantSeries = nonCompliantQuery.resultSet?.series() ?? [];

  const countsAndEfficiency = aggregateResults(
    mergeSeriesToMap(compliantSeries, nonCompliantSeries),
  );

  const error = compliantQuery.error || nonCompliantQuery.error;

  return {
    data: countsAndEfficiency,
    isLoading: compliantQuery.isLoading || nonCompliantQuery.isLoading,
    error,
  };
}
